package com.essntl.features.service.ui.addeditservice

import com.essntl.core.gpt.domain.OpenAIClient
import com.essntl.core.utils.utils.compareTables
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.area.domain.repository.AreaRepository
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.file.domain.repository.FileRepository
import com.essntl.features.file.ui.FileState
import com.essntl.features.file.ui.FileViewModel
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import com.essntl.features.service.domain.model.service.ServiceModel
import com.essntl.features.service.domain.repository.ServiceRepository
import com.essntl.features.supplier.domain.repository.SupplierRepository
import com.essntl.features.tag.domain.model.TagModel
import com.essntl.features.file.domain.model.FileModel
import com.essntl.features.service.domain.model.supplierservice.SupplierServiceModel
import com.essntl.features.tag.domain.repository.TagRepository
import com.essntl.toastmanager.ToastManager
import com.mohamedrejeb.richeditor.model.RichTextState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AddEditServiceViewModel(
    private val serviceRepository: ServiceRepository,
    private val categoryRepository: CategoryRepository,
    private val supplierRepository: SupplierRepository,
    private val tagRepository: TagRepository,
    fileRepository: FileRepository,
    private val areaRepository: AreaRepository,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val toastManager: ToastManager,
    private val openAIClient: OpenAIClient,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
    val state: MutableStateFlow<AddEditServiceState>,
) : ViewModel {

    private val imageViewModel = FileViewModel(
        fileRepository = fileRepository,
        scope = scope,
        loadingManager = loadingManager,
    )

    init {
        collectImageState()

        getAllTagList()
        getAllCategoryList()
        getAllAreaList()
        getCurrentProfile()

        // Get Current User Profile Use Case
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getProfile },
            setState = { newState -> state.update { it.copy(getProfile = newState) } },
        )
    }

    fun onEvent(event: AddEditServiceEvent) {
        when (event) {
            is AddEditServiceEvent.UpdateServiceFields -> {
                updateServiceFields(event)
                state.update { it.copy(hasChanges = true) }
            }
            is AddEditServiceEvent.CheckRichTextChanges -> {
                checkRichTextChanged(event)
                state.update { it.copy(hasChanges = true) }
            }
            is AddEditServiceEvent.SetEditMode -> {
                state.update { it.copy(isEditMode = event.enabled) }
            }
            is AddEditServiceEvent.SetHasChanges -> {
                state.update { it.copy(hasChanges = event.hasChanges) }
            }
            is AddEditServiceEvent.GetService -> getService(event)
            is AddEditServiceEvent.ToggleImageThumbnail -> toggleImageThumbnail(event)
            is AddEditServiceEvent.GetAllCategoryList -> getAllCategoryList()
            is AddEditServiceEvent.AddService -> createService(event)
            is AddEditServiceEvent.EditService -> editService(event)
            is AddEditServiceEvent.AddTag -> addTag(event)
            is AddEditServiceEvent.ClearService -> clearService()
            is AddEditServiceEvent.ClearSuccessState -> clearSuccessState()
            is AddEditServiceEvent.AddImages -> addImages(event)
            is AddEditServiceEvent.DeleteImage -> deleteImage(event)
            is AddEditServiceEvent.ClearImages -> clearImages()
            is AddEditServiceEvent.RewriteWithAI -> rewriteWithAI(event)
            is AddEditServiceEvent.AddSupplier -> addSupplier(event)
            is AddEditServiceEvent.RemoveSupplier -> removeSupplier(event)
        }
    }

    private fun addSupplier(event: AddEditServiceEvent.AddSupplier) =
        getData(
            getData = { supplierRepository.get(event.supplier.id) },
            getState = { state.value.getSupplier },
            setState = { data -> state.update { it.copy(getSupplier = data) } },
            onSuccess = { supplier ->
                val supplierService =
                    state.value.getAllSuppliersService.data
                        .orEmpty()
                        .find {
                            it.supplier.id == supplier.id &&
                                it.service.id == state.value.service.value.id
                        }

                if (supplierService != null) return@getData

                supplier.contact?.organization?.let { o ->
                    addServiceSupplier(
                        SupplierServiceModel.empty.copy(
                            service = state.value.service.value,
                            supplier = supplier,
                            organization = o,
                        ),
                    )
                }
            },
        )

    private fun addServiceSupplier(supplierService: SupplierServiceModel) =
        getData(
            getData = { serviceRepository.insertSupplierService(supplierService) },
            getState = { state.value.addSupplierService },
            setState = { data -> state.update { it.copy(addSupplierService = data) } },
            onSuccess = { supplierService ->
                state.update {
                    it.copy(
                        getAllSuppliersService = it.getAllSuppliersService.copy(
                            data = it.getAllSuppliersService.data.orEmpty() + supplierService,
                        ),
                    )
                }
            },
        )

    private fun removeSupplier(event: AddEditServiceEvent.RemoveSupplier) {
        getData(
            getData = { serviceRepository.deleteSupplierService(event.supplierService) },
            getState = { state.value.deleteSupplierService },
            setState = { data -> state.update { it.copy(deleteSupplierService = data) } },
            onSuccess = {
                state.update {
                    it.copy(
                        getAllSuppliersService = it.getAllSuppliersService.copy(
                            data = it.getAllSuppliersService.data
                                .orEmpty()
                                .filter { s ->
                                    s.id != event.supplierService.id
                                },
                        ),
                    )
                }
            },
        )
    }

    private var getSharedImageStateJob: Job? = null
    private fun collectImageState() {
        // collecting imageState
        getSharedImageStateJob?.cancel()
        getSharedImageStateJob = scope.launch {
            imageViewModel.state.collect { data ->
                isServiceChanged(
                    description = state.value.service.value.description ?: "",
                    imageState = data,
                )
            }
        }
    }

    fun isServiceChanged(
        description: String,
        imageState: FileState? = null,
    ) {
        // Compare images
        val currentImageState = imageState ?: state.value.imageState
        val oldImages = currentImageState.getAllFile.data?.map { it.id }.orEmpty()
        val newImages = state.value.service.value.images.orEmpty().toList()

        // Compare descriptions
        val oldDescription = RichTextState().setHtml(state.value.getService.data?.description.orEmpty()).toHtml()
        val newDescription = RichTextState().setHtml(description).toHtml()

        // Compare services
        val oldService = state.value.getService.data ?: ServiceModel.empty
        val newService = state.value.service.value
        val changedFields = oldService.getChangedFields(newService)

        state.update {
            it.copy(
                imageState = currentImageState,
                hasChanges = !changedFields.isEmpty ||
                    compareTables(oldImages, newImages) ||
                    oldDescription != newDescription
            )
        }
    }

    private fun rewriteWithAI(event: AddEditServiceEvent.RewriteWithAI) {
        state.update {
            it.copy(
                rewriteWithAI = State.loading(),
            )
        }

        scope.launch {
            val rewriteWithAIRequest = async {
                openAIClient.rewriteDescription(event.description)
            }

            rewriteWithAIRequest
                .await()
                .onSuccess { aiDescription ->
                    state.update {
                        it.copy(
                            rewriteWithAI = State.success(aiDescription),
                        )
                    }
                }
                .onFailure {
                    toastManager.error(
                        title = "Sorry",
                        description = it.message ?: "Could not generate description",
                    )
                }
        }
    }

    private fun addImages(event: AddEditServiceEvent.AddImages) {
        val images = event.images.filter { it.id.isNotEmpty() }
        if (images.isNotEmpty())
            state.value.service.value = state.value.service.value.copy(
                images = state.value.service.value.images.orEmpty() + images.map { it.id }.toSet(),
            )
        imageViewModel.addFiles(event.images)
    }

    private fun deleteImage(event: AddEditServiceEvent.DeleteImage) {
        imageViewModel.deleteFile(event.index)
        isServiceChanged(state.value.service.value.description ?: "")
        state.update {
            it.copy(
                thumbnailIndex = when {
                    state.value.thumbnailIndex == event.index ->
                        0

                    state.value.thumbnailIndex > event.index ->
                        state.value.thumbnailIndex - 1

                    else ->
                        state.value.thumbnailIndex
                },
            )
        }

        state.value.service.value = state.value.service.value.copy(
            images = state.value.service.value.images
                ?.filterIndexed { index, _ ->
                    index != event.index
                }
                ?.toSet(),
        )
    }

    private fun clearImages() = imageViewModel.setState(State(data = emptyList()))


    private fun updateServiceFields(event: AddEditServiceEvent.UpdateServiceFields) {
        state.value.service.value = event.service
        isServiceChanged(state.value.service.value.description ?: "")
    }

    private fun checkRichTextChanged(event: AddEditServiceEvent.CheckRichTextChanges) {
        val currentDescription = state.value.service.value.description ?: ""
        if (event.description != currentDescription) {
            state.update { it.copy(hasChanges = true) }
        }
    }

    private var getServiceJob: Job? = null
    private fun getService(event: AddEditServiceEvent.GetService) {
        if (state.value.getService.data?.id == event.serviceId) return

        imageViewModel.setState(State())

        // Make sure to cancel the previous job
        // The user can switch between services quickly before the job is finished,
        // In that case we don't want to show the result of the previous job
        getServiceJob?.cancel()
        getServiceJob = getData(
            getData = { serviceRepository.get(event.serviceId) },
            getState = { state.value.getService },
            setState = { newState ->
                state.update {
                    it.copy(
                        getService = newState,
                        isEditMode = false,  // Updated from isEditEnabled
                        hasChanges = false
                    )
                }
            },
            onSuccess = { service ->
                state.value.service.value = service

                getSuppliers(service.id)

                val thumbnailIndex =
                    service.images.orEmpty()
                        .indexOfFirst { imageId ->
                            imageId == service.primaryImage?.id
                        }
                        .let {
                            if (it == -1 && service.images.orEmpty().isNotEmpty())
                                0
                            else
                                it
                        }

                state.update {
                    it.copy(
                        thumbnailIndex = thumbnailIndex,
                    )
                }

                val images = service.images.orEmpty()
                if (images.isNotEmpty())
                    imageViewModel.getFiles(images.toList(), true)
                else
                    imageViewModel.setState(State.success(emptyList()))
            },
        )
    }

    private fun getSuppliers(serviceId: String) =
        getData(
            getData = { serviceRepository.getServiceSuppliers(serviceId) },
            getState = { state.value.getAllSuppliersService },
            setState = { newState -> state.update { it.copy(getAllSuppliersService = newState) } },
        )

    private fun toggleImageThumbnail(event: AddEditServiceEvent.ToggleImageThumbnail) {
        state.update {
            it.copy(
                thumbnailIndex = if (event.index == state.value.thumbnailIndex) 0 else event.index,
            )
        }
    }

    private fun getAllTagList() =
        getData(
            getData = { tagRepository.getAll() },
            getState = { state.value.getAllTag },
            setState = { newState -> state.update { it.copy(getAllTag = newState) } },
        )

    private fun getAllCategoryList() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { newState -> state.update { it.copy(getAllCategory = newState) } },
        )

    private fun getAllAreaList() =
        getData(
            getData = { areaRepository.getAll() },
            getState = { state.value.getAllArea },
            setState = { newState -> state.update { it.copy(getAllArea = newState) } },
        )

    private fun createService(event: AddEditServiceEvent.AddService) {
        if (state.value.addEditService.isLoading) return

        state.update {
            it.copy(
                addEditService = State.loading(),
            )
        }

        state.value.service.value = state.value.service.value.copy(
            description = event.description,
        )



        scope.launch {
            val insertRequest = async {
                imageViewModel.insertFiles(
                    folderName = "services",
                    bucketName = "images",
                    entityType = "service",
                    context = event.context,
                    onSuccess = { images ->
                        state.value.service.value = state.value.service.value.copy(
                            images = state.value.service.value.images.orEmpty() + images.map { it.id }.toSet(),
                        )
                    },
                    onFailure = {
                        state.update {
                            it.copy(
                                addEditService = State.failure(),
                            )
                        }
                    },
                )
            }

            val insertResult = insertRequest.await()
            if (!insertResult) return@launch

            val images = state.value.imageState.getAllFile.data.orEmpty().toList()
            state.update {
                it.copy(
                    thumbnailIndex = if (state.value.thumbnailIndex in images.indices) state.value.thumbnailIndex else 0,
                )
            }

            if (!images.isEmpty()) {
                state.value.service.value = state.value.service.value.copy(
                    primaryImage = images.getOrNull(state.value.thumbnailIndex) ?: FileModel.empty,
                )
            }

            getDataSuspend(
                getData = { serviceRepository.insert(state.value.service.value) },
                getState = { state.value.addEditService },
                setState = { newState -> state.update { it.copy(addEditService = newState) } },
                onSuccess = {
                    toastManager.success(
                        title = "Saved",
                        description = "New service has been saved",
                    )
                },
                ignoreLoading = true,
            )
        }
    }

    private fun editService(event: AddEditServiceEvent.EditService) {
        state.value.getService.data ?: return

        state.update {
            it.copy(
                addEditService = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)

            val insertRequest = async {
                imageViewModel.insertFiles(
                    folderName = "services",
                    bucketName = "images",
                    entityType = "service",
                    context = event.context,
                    onSuccess = { images ->
                        state.value.service.value = state.value.service.value.copy(
                            images = state.value.service.value.images.orEmpty() + images.map { it.id },
                        )
                    },
                    onFailure = {
                        state.update {
                            it.copy(
                                addEditService = State.failure(),
                            )
                        }
                    },
                )
            }
            val insertResult = insertRequest.await()
            if (!insertResult) return@launch

            val images = state.value.imageState.getAllFile.data.orEmpty().toList()
            state.update {
                it.copy(
                    thumbnailIndex = if (state.value.thumbnailIndex in images.indices) state.value.thumbnailIndex else 0,
                )
            }

            state.value.service.value = state.value.service.value.copy(
                primaryImage = images.getOrNull(state.value.thumbnailIndex) ?: FileModel.empty,
            )

            val getService = state.value.getService.data ?: return@launch
            val newService =
                getService.getChangedFields(
                    state.value.service.value.copy(
                        description = event.description,
                    ),
                )

            if (newService.isEmpty) return@launch

            serviceRepository.update(newService)
                .onSuccess { service ->
                    state.update {
                        it.copy(
                            addEditService = State.success(service),
                            getService = State.success(service),
                        )
                    }.also {
                        state.value.service.value = service
                    }

                    toastManager.success(
                        title = "Updated",
                        description = "Service has been updated",
                    )
                }
                .onFailure {
                    state.update {
                        it.copy(
                            addEditService = State.failure(),
                        )
                    }
                }
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(true)
            }
            state.update {
                it.copy(
                    addEditService = it.addEditService.copy(
                        isLoading = false,
                    ),
                )
            }
        }
    }

    private fun addTag(event: AddEditServiceEvent.AddTag) {
        if (state.value.addTagLoadingMap.any { it.equals(event.name, true) }) return
        if (state.value.getAllTag.data.orEmpty().any { it.name.equals(event.name, true) }) return

        val newTag = TagModel(
            id = "",
            name = event.name,
        )

        state.update {
            it.copy(
                getAllTag = it.getAllTag.copy(
                    isLoading = true,
                ),
                addTagLoadingMap = it.addTagLoadingMap.toMutableSet().also { set ->
                    set.add(event.name)
                },
            )
        }

        scope.launch {
            tagRepository.insert(newTag)
                .onSuccess { tag ->
                    state.update {
                        val newTagList = it.getAllTag.data.orEmpty().toMutableList().also { list ->
                            list.add(0, tag)
                        }
                        val newServiceTagIds = it.service.value.tags.orEmpty().toMutableSet().also { set ->
                            set.add(tag.id)
                        }
                        it.copy(
                            getAllTag = it.getAllTag.copy(
                                isLoading = false,
                                data = newTagList,
                            ),
                            addTagLoadingMap = it.addTagLoadingMap.toMutableSet().also { set ->
                                set.remove(event.name)
                            },
                        ).also { state ->
                            state.service.value = state.service.value.copy(
                                tags = newServiceTagIds,
                            )
                        }
                    }

                    isServiceChanged(state.value.service.value.description ?: "")
                }
                .onFailure {
                    state.update {
                        it.copy(
                            getAllTag = it.getAllTag.copy(
                                isLoading = false,
                            ),
                        )
                    }
                }
        }
    }

    private fun clearService() {
        state.value = AddEditServiceState(
            getAllCategory = state.value.getAllCategory,
            getAllTag = state.value.getAllTag,
            getAllArea = state.value.getAllArea,
            getService = State(),
            addEditService = State(),
            isEditMode = false,
            hasChanges = false,
            getProfile = state.value.getProfile  // Preserve the profile state
        )

        imageViewModel.setState(State())
        state.value.service.value = ServiceModel.empty
    }

    private fun clearSuccessState() {
        state.update {
            it.copy(
                addEditService = State(),
                isEditMode = false,
                hasChanges = false
            )
        }
    }

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

}
